import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { hasAccess } from '../redux/selectors/authSelectors';
import useAccountInitials from '../hooks/useAccountInitials';
import { initialsFromFullName } from '../utils/account';
import { 
  Search, 
  Calendar, 
  MessageCircle, 
  Send, 
  Bell,
  ChevronDown,
  BookCheck
} from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // <-- Add this import
type TopBarProps = {
  onHandleQuickSend: () => void;
};
//const TopBar: React.FC<TopBarProps>= ({ onOpenQuickSend }: { onOpenQuickSend: () => void }) => {
const TopBar: React.FC<TopBarProps>= ({ onHandleQuickSend }) => {
  const navigate = useNavigate(); // <-- Add this hook
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const canAccessTeamManagement = useSelector((state: RootState) => hasAccess(state, 'team_management'));
  const accountName = useSelector((state: RootState) => state.auth.user?.user_name ?? '');
  const accountEmail = useSelector((state: RootState) => state.auth.user?.user_email ?? '');
  // Falls back to the login response's own name (first + last word) while the
  // profile request is in flight, then to a single letter, then to '?'.
  const nameFallback =
    initialsFromFullName(accountName) ||
    accountEmail.trim().charAt(0).toUpperCase() ||
    '?';
  const accountInitial = useAccountInitials(nameFallback);
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Search Bar */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search anything..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border-0 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
            />
          </div>
        </div>

        {/* Right Side Icons */}
        <div className="flex items-center space-x-4">
          {/* Calendar Icon */}
          {false&&<button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => navigate('/calendar')}>
            <Calendar className="w-5 h-5" />
          </button>}

          {/* Message Square Icon */}
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => navigate('/task')}
          >
            <BookCheck className="w-5 h-5" />
          </button>

          {/* Message Circle Icon */}
          {false&&<button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <MessageCircle className="w-5 h-5" />
          </button>}

          {/* Send Icon */}
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" onClick={onHandleQuickSend}>
            <Send className="w-5 h-5" />
          </button>

          {/* Notification Bell */}
          <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          {/* Profile Dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded-lg p-1"
              onClick={() => setOpen((v) => !v)}
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
            >
              {/* Was a fixed Pexels stock photo, so every signed-in user saw
                  the same stranger's face. There is no avatar image on the
                  account, so show the same initials My Profile shows — "RL" for
                  Ravva … Likith — instead of someone else's portrait. */}
              <div
                className={`grid h-8 w-8 place-items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-sm font-semibold text-white transition-transform duration-200 ${
                  open ? 'scale-[1.02]' : ''
                }`}
                title={accountName || accountEmail || 'Signed in'}
              >
                {accountInitial}
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown panel */}
            <div
              className={`absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 transition-all duration-200 ease-out ${
                open ? 'scale-100 opacity-100' : 'pointer-events-none scale-95 opacity-0'
              }`}
              role="menu"
            >
              <div className="py-1">
                <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => { setOpen(false); navigate('/profile'); }}
                  role="menuitem"
                >
                  My Profile
                </button>
                {canAccessTeamManagement && <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => { setOpen(false); navigate('/company/team-managment'); }}
                  role="menuitem"
                >
                  Team Management
                </button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;